import { useState, useEffect, useRef } from 'react';

/**
 * A simplified hook that combines delayed loading and persistent content
 * to create smooth transitions without loading flickers
 */
export function useSmoothLoading<T>(
  data: T | null | undefined,
  loading: boolean,
  delay: number = 300
) {
  const [showLoading, setShowLoading] = useState(false);
  const [displayData, setDisplayData] = useState<T | null | undefined>(data);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);
  const lastDataRef = useRef<T | null | undefined>(data);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }

    if (loading) {
      // If this is the very first load, show loading immediately
      if (!hasLoadedOnce) {
        setShowLoading(true);
      } else if (displayData && Array.isArray(displayData) && displayData.length > 0) {
        // If we have existing data with content, keep showing it during the new load
        setShowLoading(false);
      } else if (displayData && !Array.isArray(displayData) && displayData) {
        // If we have existing non-array data, keep showing it during the new load
        setShowLoading(false);
      } else {
        // For loads without existing meaningful data, use a delay to prevent flickers
        timeoutRef.current = window.setTimeout(() => {
          setShowLoading(true);
        }, delay);
      }
    } else {
      // Loading finished
      setShowLoading(false);
      setHasLoadedOnce(true);
      
      // Only update display data if the new data is different or we don't have data yet
      if (data !== lastDataRef.current || !displayData) {
        setDisplayData(data);
        lastDataRef.current = data;
      }
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [data, loading, delay, hasLoadedOnce, displayData]);

  // Immediately update display data when we get new data and we're not loading
  useEffect(() => {
    if (!loading && data !== undefined && data !== lastDataRef.current) {
      setDisplayData(data);
      lastDataRef.current = data;
    }
  }, [data, loading]);

  return {
    showLoading,
    displayData,
    isLoading: loading,
    hasLoadedOnce
  };
}